import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { CheckCheck } from "lucide-react";

import {
  useMarkAllAsRead,
  useMarkAsRead,
  useNotificationList,
} from "@/features/notifications/model/useNotifications";
import { useNotificationsStore } from "@/features/notifications/model/notificationsStore";
import type { Notification } from "@/features/notifications/types";
import { Button } from "@/shared/ui/Button";
import { cn } from "@/shared/lib/cn";

function relativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

export function NotificationDrawer() {
  const drawerOpen = useNotificationsStore((s) => s.drawerOpen);
  const closeDrawer = useNotificationsStore((s) => s.closeDrawer);
  const { data: notifications, isLoading } = useNotificationList();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click. The backdrop swallows clicks outside the panel.
  // We rely on a sibling backdrop instead of pointerdown listeners so the
  // panel itself never has to track event propagation.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  if (!drawerOpen) return null;

  const items = notifications ?? [];
  const hasItems = items.length > 0;
  const hasUnread = items.some((n) => n.read_at === null);

  const handleItemClick = (n: Notification) => {
    if (n.read_at === null) {
      markAsRead.mutate(n.id);
    }
    closeDrawer();
    if (n.link_url) {
      navigate(n.link_url);
    }
  };

  return (
    <>
      {/* Backdrop — click outside closes. Transparent so it doesn't dim the page. */}
      <div
        aria-hidden
        className="fixed inset-0 z-40"
        onClick={closeDrawer}
      />

      {/* Popover panel.
          Mobile: anchored to the viewport (fixed, side-margins) so the panel
          always fits the screen. The bell sits inside header padding with the
          Sign-out button to its right, so an absolute, bell-anchored 360px
          panel runs its left edge off-screen on narrow viewports.
          Desktop (sm+): reverts to a bell-anchored dropdown. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notifications"
        className={cn(
          "fixed left-2 right-2 top-[3.75rem] z-50 overflow-hidden rounded-lg border border-border bg-surface shadow-card",
          "sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[360px] sm:max-w-[90vw]",
          "flex flex-col",
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <p className="text-sm font-semibold text-copy">Notifications</p>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
              className="gap-1.5"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-copy-muted">
              Loading…
            </div>
          ) : !hasItems ? (
            <div className="px-4 py-10 text-center text-sm text-copy-muted">
              You&rsquo;re all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => {
                const unread = n.read_at === null;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(n)}
                      className={cn(
                        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors",
                        "hover:bg-surface-sunken",
                        "focus-visible:outline-none focus-visible:bg-surface-sunken",
                        unread && "bg-primary-light",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                          unread ? "bg-primary" : "bg-transparent",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className="truncate text-sm font-medium text-copy"
                          title={n.title}
                        >
                          {n.title}
                        </p>
                        <p
                          className="line-clamp-2 text-xs text-copy-muted"
                          title={n.body}
                        >
                          {n.body}
                        </p>
                        <p className="mt-1 text-[11px] text-copy-subtle">
                          {relativeTime(n.created_at)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
